import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import { CSpinner, CButton } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
  cilMediaPlay,
  cilMediaPause,
  cilFullscreen,
  cilVolumeHigh,
  cilVolumeOff,
} from '@coreui/icons';
import i18n from "../../../i18n";

// i18n.t('custom_video_player.playback_rates_description', 'Скорости воспроизведения вплоть до 16x')
const PLAYBACK_RATES = [0.25, 0.5, 0.75, 1.0, 1.25, 1.5, 2.0, 4.0, 8.0, 16.0];

const formatDuration = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '00:00:00';

  const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
  const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');

  return h === '00' ? `${m}:${s}` : `${h}:${m}:${s}`;
};

const CustomVideoPlayer = forwardRef((props, ref) => {
  const {
    url,
    initialSeekTime,
    seekTriggerId,
    isLoading,
    onProgress,
    onReady,
    onEnded,
    children,
  } = props;

  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const hasAutoPlayedRef = useRef(false);

  const [isReady, setIsReady] = useState(false);
  const [state, setState] = useState({
    playing: false,
    volume: 0.8,
    muted: false,
    played: 0,
    duration: 0,
    seeking: false,
    playbackRate: 1.0, // выбранная скорость сохраняется при смене видео
  });

  useImperativeHandle(ref, () => ({
    getInternalPlayer: () =>
      playerRef.current ? playerRef.current.getInternalPlayer() : null,
    setPlaybackRate: (rate) => setState((s) => ({ ...s, playbackRate: rate })),
    togglePlay: () => setState((s) => ({ ...s, playing: !s.playing })),
    play: () => setState((s) => ({ ...s, playing: true })),
    pause: () => setState((s) => ({ ...s, playing: false })),
  }));

  // Сброс прогресса при смене URL (скорость playbackRate сохраняется)
  useEffect(() => {
    setIsReady(false);
    hasAutoPlayedRef.current = false;
    setState((s) => ({ ...s, played: 0, duration: 0, playing: false }));
  }, [url]);

  // Автоплей после загрузки видео
  useEffect(() => {
    if (isReady && isLoading === false && !hasAutoPlayedRef.current) {
      hasAutoPlayedRef.current = true;
      setState((s) => ({ ...s, playing: true }));
    }
  }, [isReady, isLoading]);

  // Перемотка (Seek)
  useEffect(() => {
    if (isReady && playerRef.current && initialSeekTime !== null) {
      playerRef.current.seekTo(initialSeekTime, 'seconds');
    }
  }, [seekTriggerId, isReady]);

  // --- Обработчики событий ---

  const handleRateSelect = (e) => {
    const nextRate = parseFloat(e.target.value);
    setState((s) => ({ ...s, playbackRate: nextRate }));
  };

  const handleSeekMouseDown = () => setState((s) => ({ ...s, seeking: true }));
  const handleSeekChange = (e) =>
    setState((s) => ({ ...s, played: parseFloat(e.target.value) }));
  const handleSeekMouseUp = (e) => {
    setState((s) => ({ ...s, seeking: false }));
    playerRef.current.seekTo(parseFloat(e.target.value), 'fraction');
  };

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  };

  const togglePlay = () => setState((s) => ({ ...s, playing: !s.playing }));
  const toggleMute = () => setState((s) => ({ ...s, muted: !s.muted }));

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setState((s) => ({
      ...s,
      volume: newVolume,
      muted: newVolume === 0,
    }));
  };

  const handlePlayerReady = (p) => {
    setIsReady(true);
    if (onReady) onReady(p.getInternalPlayer());
  };

  const handlePlayerProgress = (p) => {
    if (!state.seeking) setState((s) => ({ ...s, played: p.played }));
    if (onProgress) onProgress(p.playedSeconds);
  };

  const handleVideoEnded = () => {
    setState((s) => ({ ...s, playing: false }));
    if (onEnded) {
      onEnded();
    }
  };

  // --- Стили ---

  const containerStyle = {
    minHeight: '200px',
    overflow: 'hidden',
    borderRadius: '4px',
    lineHeight: 0,
  };

  const overlayStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    zIndex: 20,
    opacity: isReady ? 1 : 0.5,
    transition: 'opacity 0.3s',
  };

  const currentTime = state.duration * state.played;
  const showMutedIcon = state.muted || state.volume === 0;

  return (
    <div
      ref={containerRef}
      className="position-relative w-100 bg-black d-flex align-items-center justify-content-center"
      style={containerStyle}
    >
      {!isReady && (
        <div className="position-absolute" style={{ zIndex: 10 }}>
          <CSpinner color="light" />
        </div>
      )}

      <ReactPlayer
        ref={playerRef}
        className="react-player"
        width="100%"
        height="100%"
        url={url}
        playing={state.playing}
        playbackRate={state.playbackRate}
        volume={state.volume}
        muted={state.muted}
        progressInterval={50}
        onReady={handlePlayerReady}
        onProgress={handlePlayerProgress}
        onDuration={(d) => setState((s) => ({ ...s, duration: d }))}
        onEnded={handleVideoEnded}
        config={{
          file: {
            attributes: {
              crossOrigin: 'anonymous',
              playsInline: true,
              style: {
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'contain',
              },
            },
          },
        }}
      />

      {isReady && children}

      <div className="p-2 w-100" style={overlayStyle}>
        <input
          type="range"
          min={0}
          max={0.99999}
          step="any"
          value={state.played}
          onMouseDown={handleSeekMouseDown}
          onChange={handleSeekChange}
          onMouseUp={handleSeekMouseUp}
          disabled={!isReady}
          style={{
            width: '100%',
            accentColor: '#321fdb',
            cursor: isReady ? 'pointer' : 'not-allowed',
            height: '4px',
          }}
        />

        <div className="d-flex justify-content-between align-items-center text-white mt-1">
          <div className="d-flex align-items-center gap-3">
            <CButton
              color="link"
              className="text-white p-0"
              onClick={togglePlay}
              disabled={!isReady}
            >
              <CIcon icon={state.playing ? cilMediaPause : cilMediaPlay} size="lg" />
            </CButton>

            <div className="d-flex align-items-center gap-1 d-none d-md-flex">
              <CButton
                color="link"
                className="text-white p-0"
                onClick={toggleMute}
                disabled={!isReady}
              >
                <CIcon icon={showMutedIcon ? cilVolumeOff : cilVolumeHigh} />
              </CButton>
              <input
                type="range"
                min={0}
                max={1}
                step="any"
                value={state.muted ? 0 : state.volume}
                onChange={handleVolumeChange}
                disabled={!isReady}
                style={{ width: '60px', accentColor: '#fff', height: '3px' }}
              />
            </div>

            <span className="small font-monospace text-light">
              {formatDuration(currentTime)} / {formatDuration(state.duration)}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Выпадающий список скорости от 0.25x до 16x */}
            <select
              value={state.playbackRate}
              onChange={handleRateSelect}
              disabled={!isReady}
              aria-label={i18n.t('custom_video_player.playback_speed', 'Скорость воспроизведения')}
              style={{
                backgroundColor: 'rgba(20, 20, 20, 0.75)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: isReady ? 'pointer' : 'not-allowed',
                outline: 'none',
              }}
            >
              {PLAYBACK_RATES.map((rate) => (
                <option
                  key={rate}
                  value={rate}
                  style={{ backgroundColor: '#1e1e1e', color: '#fff' }}
                >
                  {rate}x
                </option>
              ))}
            </select>

            <CButton
              color="link"
              className="text-white p-0 ms-1"
              onClick={toggleFullscreen}
              disabled={!isReady}
            >
              <CIcon icon={cilFullscreen} />
            </CButton>
          </div>
        </div>
      </div>
    </div>
  );
});

CustomVideoPlayer.displayName = 'CustomVideoPlayer';

CustomVideoPlayer.propTypes = {
  url: PropTypes.string,
  initialSeekTime: PropTypes.number,
  seekTriggerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  onProgress: PropTypes.func,
  onReady: PropTypes.func,
  onEnded: PropTypes.func,
  children: PropTypes.node,
};

CustomVideoPlayer.defaultProps = {
  url: '',
  initialSeekTime: null,
  seekTriggerId: null,
  isLoading: false,
  onProgress: null,
  onReady: null,
  onEnded: null,
  children: null,
};

export default CustomVideoPlayer;